import { T, useGT } from 'gt-react';
import { ArrowUUpLeftIcon, RobotIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';
import { useListActionConsumers } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';

interface ActionUsageBadgeProps {
  /** Distinct `workflow_action` steps calling this action, from other workflows. */
  workflowConsumerCount: number;
  /** AR collections portal-action bindings on a customer profile. */
  portalActionBindingCount: number;
  /** Extra classes for the wrapper (icon sizing is fixed). */
  className?: string;
}

/**
 * "This action is used elsewhere" — a compact count of everything that depends
 * on one action lineage, with the breakdown in a tooltip.
 *
 * Presentational on purpose: the workflows list already has the counts on its
 * summary rows, while a call site has to fetch them (see
 * `CalledActionUsageBadge`). Renders nothing when nothing depends on the action,
 * so it reads as a flag rather than a stat.
 */
export function ActionUsageBadge({
  workflowConsumerCount,
  portalActionBindingCount,
  className,
}: ActionUsageBadgeProps) {
  const gt = useGT();
  const total = workflowConsumerCount + portalActionBindingCount;
  if (total === 0) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('inline-flex items-center gap-1', className)}>
          <Badge variant="outline" className="gap-1 text-xs">
            <ArrowUUpLeftIcon className="size-3" />
            {total}
          </Badge>
          {/* A portal binding lives in another module and is the consumer most
              likely to be forgotten, so it gets its own mark. */}
          {portalActionBindingCount > 0 && (
            <Badge variant="outline" className="gap-1 text-xs">
              <RobotIcon className="size-3" />
              {portalActionBindingCount}
            </Badge>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-xs">
        <p className="font-medium">
          <T context="tooltip title for the action usage badge">
            Used elsewhere
          </T>
        </p>
        <p className="opacity-80">
          {workflowConsumerCount > 0 &&
            gt('{count} workflow step(s) call this action.', {
              context: 'action usage tooltip, workflow consumers',
              count: workflowConsumerCount,
            })}{' '}
          {portalActionBindingCount > 0 &&
            gt('{count} customer profile(s) bind it as a portal action.', {
              context: 'action usage tooltip, portal action bindings',
              count: portalActionBindingCount,
            })}
        </p>
        <p className="mt-1 opacity-60">
          <T context="tooltip hint for the action usage badge">
            Editing or unpublishing it affects all of them.
          </T>
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The same badge for a `workflow_action` step — "the action you're calling is
 * shared", so tailoring it to this flow breaks the other callers.
 *
 * Counts *other* consumers: this call site is itself one of them, so it's
 * excluded, and the badge disappears on an action only this workflow uses.
 * Reuses the `listActionConsumers` query the action_input drawer already
 * populates, keyed by lineage, so several call sites on one canvas share a fetch.
 */
export function CalledActionUsageBadge({
  lineageWorkflowId,
  selfStepId,
  className,
}: {
  /** Lineage id of the action this step calls. */
  lineageWorkflowId: string;
  /** This step's id, so the badge doesn't count the call site it sits on. */
  selfStepId: string;
  className?: string;
}) {
  const { data } = useListActionConsumers(lineageWorkflowId);
  const others = (data?.consumers ?? []).filter(
    (consumer) => consumer.stepId !== selfStepId,
  );

  return (
    <ActionUsageBadge
      workflowConsumerCount={others.length}
      portalActionBindingCount={data?.portalActionBindings.length ?? 0}
      className={className}
    />
  );
}
